/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { metaTagKey } from '@/constants/seoMetaTags';
import { type ContentStackPost } from '@/features/contentstack/interface';
import { useLocale } from '@/hooks/useLocale';
import { getBaseSchemaUrl } from '@/utils/common';
import Head from 'next/head';
import { getArticleSchema } from '../../lib/utils/getArticleSchema';
import { type FormattedContentStackPost } from '@/features/contentstack/interface/models';

export type CMSBlogSeoTagsProps = {
  currentURL: string;
  seoData: NonNullable<ContentStackPost['seo']>;
  blogData: FormattedContentStackPost['blogContent'] | undefined;
};

export const CMSBlogSeoTags = ({ currentURL, seoData, blogData }: CMSBlogSeoTagsProps) => {
  const locale = useLocale();
  const baseURL = getBaseSchemaUrl(locale);
  const fullCurrentURL = `${baseURL}${currentURL}`;

  const canonicalURL = seoData.canonical_url || fullCurrentURL;

  const articleSchema = blogData
    ? getArticleSchema({
        blogData,
        currentURL,
        baseURL,
      })
    : null;

  return (
    <Head>
      <title key={metaTagKey('title')}>{seoData.page_title}</title>
      <meta key={metaTagKey('author')} name="author" content="AutoZone" />
      <meta key={metaTagKey('og:type')} property="og:type" content="article" />
      {seoData.page_title ? (
        <meta key={metaTagKey('og:title')} property="og:title" content={seoData.page_title} />
      ) : null}
      {seoData.meta_description ? (
        <>
          <meta
            key={metaTagKey('description')}
            name="description"
            content={seoData.meta_description}
          />
          <meta
            key={metaTagKey('og:description')}
            property="og:description"
            content={seoData.meta_description}
          />
        </>
      ) : null}
      <meta key={metaTagKey('og:url')} property="og:url" content={fullCurrentURL} />
      {seoData.social_media?.post_image ? (
        <meta
          key={metaTagKey('og:image')}
          property="og:image"
          content={seoData.social_media.post_image}
        />
      ) : null}
      {canonicalURL ? (
        <link key={metaTagKey('canonical')} rel="canonical" href={canonicalURL} />
      ) : null}

      {articleSchema ? (
        <script
          key={metaTagKey('articleSchema')}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
      ) : null}
    </Head>
  );
};
